import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { status } from "../../../../core/common/selectoption/selectoption";
import CommonSelect from "../../../../core/common/commonSelect";
import { useCourse } from "../hooks/useCourse";
import { CourseResponseType } from "../types/courseResponse";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller } from "react-hook-form";

const validationSchema = yup.object().shape({
  name: yup.string().required("Course name is required"),
  description: yup.string().required("Description is required"),
  stages: yup
    .number()
    .typeError("Levels must be a number")
    .required("Levels are required")
    .min(1, "Minimum 1 level required")
    .max(15, "Maximum 15 levels allowed"),
  status: yup.string().required("Status is required"),
  courseStages: yup.array().of(
    yup.object().shape({
      name: yup.string().required("Level name is required"),
      min_marks: yup
        .number()
        .typeError("Must be a number")
        .required("Min marks required")
        .min(0, "Minimum marks cannot be negative")
        .test(
          "less-than-max",
          "Min marks must be less than max marks",
          function (value) {
            return value < this.parent.max_marks;
          }
        ),
      max_marks: yup
        .number()
        .typeError("Must be a number")
        .required("Max marks required")
        .min(1, "Max marks must be at least 1"),
    })
  ),
});

const AddCourse = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const {
    addNewCourse,
    updateCourse,
    isAdding,
    isUpdating,
    addError,
    useGetCourseById,
  } = useCourse();
  const { id } = useParams<{ id: string }>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isPublished, setIsPublished] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitted, touchedFields },
    watch,
    control,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      description: "",
      stages: 0,
      status: "",
      courseStages: [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "courseStages",
  });

  const courseData = useGetCourseById(id || "");

  useEffect(() => {
    if (id && courseData) {
      const course: CourseResponseType = courseData;
      const stagesData = course.courseStages.map((stage) => ({
        name: stage.name,
        max_marks: stage.max_marks,
        min_marks: stage.min_marks || 0,
      }));

      reset({
        name: course.name || "",
        description: course.description || "",
        stages: parseInt(course.stages || "0", 10),
        status: course.status || "",
        courseStages: stagesData,
      });

      setIsPublished(course.isPublished);
      setIsEdit(true);
    } else {
      reset({
        name: "",
        description: "",
        stages: 0,
        status: "",
        courseStages: [],
      });
      setIsEdit(false);
    }
  }, [id, courseData, reset]);

  const handleStagesInputChange = (value: string) => {
    const parsedValue = parseInt(value, 10);
    if (!isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 15) {
      const currentLength = fields.length;
      if (parsedValue > currentLength) {
        append(
          Array(parsedValue - currentLength)
            .fill({})
            .map(() => ({ name: "", max_marks: 0, min_marks: 0 }))
        );
      } else if (parsedValue < currentLength) {
        remove(
          Array.from(
            { length: currentLength - parsedValue },
            (_, i) => currentLength - 1 - i
          )
        );
      }
    }
  };

  const onSubmit = async (data: any) => {
    const isValid = await trigger();

    if (!isValid) return;

    // Use stage number for order_in_course (1-based)
    const updatedCourseStages = data.courseStages.map(
      (stage: any, index: number) => ({
        ...stage,
        order_in_course: index + 1,
      })
    );

    // Send stages value as a number
    const stageCount = parseInt(data.stages.toString(), 10);

    const courseDataToSubmit = {
      ...data,
      center_id: 6,
      stages: stageCount, // Convert to number
      courseStages: updatedCourseStages,
    };
    let result;
    if (isEdit && id) {
      result = await updateCourse(id, courseDataToSubmit);
    } else {
      result = await addNewCourse(courseDataToSubmit);
    }

    if (result?.success) {
      navigate(routes.courseList);
    }
  };

  const stagesValue = watch("stages");

  useEffect(() => {
    if (stagesValue) {
      handleStagesInputChange(stagesValue.toString());
    }
  }, [stagesValue]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content content-two">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">{isEdit ? "Edit" : "Add"} Course</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.courseList}>Courses</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit" : "Add"} Course
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-calendar-event fs-16" />
                      </span>
                      <h4 className="text-dark">Course Details</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    {isPublished && (
                      <div className="alert alert-info">
                        This course is currently published. All fields are
                        read-only.
                      </div>
                    )}
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Course Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors.name ? "is-invalid" : ""
                              }`}
                            placeholder="Enter course name"
                            {...register("name")}
                            required
                            disabled={isPublished}
                          />
                          {errors.name &&
                            (isSubmitted || touchedFields.name) && (
                              <div className="invalid-feedback">
                                {errors.name.message}
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Status <span className="text-danger">*</span>
                          </label>
                          <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                              <CommonSelect
                                className="select"
                                options={status}
                                value={status.find(
                                  (option) => option.value === field.value
                                )}
                                onChange={(selectedOption) =>
                                  field.onChange(selectedOption?.value)
                                }
                                isDisabled={isPublished}
                              />
                            )}
                          />
                          {errors.status && (
                            <div className="invalid-feedback">
                              {errors.status.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">
                            No. of Levels <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            className={`form-control ${errors.stages ? "is-invalid" : ""
                              }`}
                            placeholder="Enter total levels"
                            {...register("stages")}
                            required
                            min={0}
                            max={15}
                            disabled={isPublished}
                          />
                          {errors.stages && (
                            <div className="invalid-feedback">
                              {errors.stages.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Course Description{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className={`form-control ${errors.description ? "is-invalid" : ""
                              }`}
                            rows={4}
                            placeholder="Enter course description"
                            {...register("description")}
                            required
                            disabled={isPublished}
                          />
                          {errors.description && (
                            <div className="invalid-feedback">
                              {errors.description.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {Number(stagesValue) > 0 && (
                      <div className="card">
                        <div className="card-header">
                          <h5 className="mb-0">
                            Course Levels <span className="text-danger">*</span>
                          </h5>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-bordered mt-4">
                              <thead>
                                <tr>
                                  <th>Level</th>
                                  <th>Level Name</th>
                                  <th>Max Marks</th>
                                  <th>Min Marks</th>
                                </tr>
                              </thead>
                              <tbody>
                                {fields.map((field, index) => (
                                  <tr key={field.id}>
                                    <td>{index + 1}</td>
                                    <td>
                                      <input
                                        type="text"
                                        className={`form-control ${errors.courseStages?.[index]?.name
                                            ? "is-invalid"
                                            : ""
                                          }`}
                                        placeholder="Enter level name"
                                        {...register(
                                          `courseStages.${index}.name`
                                        )}
                                        disabled={isPublished}
                                      />
                                      {errors.courseStages?.[index]?.name && (
                                        <div className="invalid-feedback">
                                          {
                                            errors.courseStages[index]?.name
                                              ?.message
                                          }
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        className={`form-control ${errors.courseStages?.[index]
                                            ?.max_marks
                                            ? "is-invalid"
                                            : ""
                                          }`}
                                        placeholder="Enter max marks"
                                        {...register(
                                          `courseStages.${index}.max_marks`
                                        )}
                                        disabled={isPublished}
                                      />
                                      {errors.courseStages?.[index]
                                        ?.max_marks && (
                                          <div className="invalid-feedback">
                                            {
                                              errors.courseStages[index]
                                                ?.max_marks?.message
                                            }
                                          </div>
                                        )}
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        className={`form-control ${errors.courseStages?.[index]
                                            ?.min_marks
                                            ? "is-invalid"
                                            : ""
                                          }`}
                                        placeholder="Enter min marks"
                                        {...register(
                                          `courseStages.${index}.min_marks`
                                        )}
                                        disabled={isPublished}
                                      />
                                      {errors.courseStages?.[index]
                                        ?.min_marks && (
                                          <div className="invalid-feedback">
                                            {
                                              errors.courseStages[index]
                                                ?.min_marks?.message
                                            }
                                          </div>
                                        )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-end mt-4">
                  <Link to={routes.courseList} className="btn btn-light me-3">
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!isDirty || isAdding || isUpdating || isPublished}
                  >
                    {isAdding
                      ? "Saving..."
                      : isUpdating
                        ? "Updating..."
                        : isEdit
                          ? "Update Course"
                          : "Create Course"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          {addError && (
            <div className="alert alert-danger mt-3">{addError}</div>
          )}
        </div>
      </div>
    </>
  );
};

export default AddCourse;
